import type { ReactNode } from "react";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { LodgingSummary, RoomInfo } from "@hopper-b2b/types";
import { DateTimeFormatStyle } from "@hopper-i18n/formatter";
import { Box, Grid } from "@material-ui/core";
import clsx from "clsx";
import type { ConfigType as DayJsInput } from "dayjs";
import { Icon, IconName } from "../Icon";
import { formatDateWithLocale } from "../utils/dateFormatting";

import HotelAddress from "./components/HotelAddress";
import HotelName from "./components/HotelName";
import HotelRoomName from "./components/HotelRoomName";
import HotelStarRating from "./components/HotelStarRating";

import "./styles.scss";

export interface IHotelSummaryPanelProps {
  actions?: ReactNode;
  checkIn: DayJsInput | null;
  checkOut: DayJsInput | null;
  className?: string;
  /** Date format style. Use DateTimeFormatStyle for locale-aware formatting (recommended).
   * String format is only supported for backward compatibility and will not use locale information. */
  dateFormat?: string | DateTimeFormatStyle;
  fullImage?: boolean;
  hideImage?: boolean;
  hideStarRating?: boolean;
  isMobile?: boolean;
  lodging: LodgingSummary;
  roomInfo?: RoomInfo;
}

export const HotelSummaryPanel = ({
  actions,
  checkIn,
  checkOut,
  className,
  dateFormat = DateTimeFormatStyle.ShortMonthDayShortWeekday,
  fullImage = false,
  hideImage = false,
  hideStarRating = false,
  isMobile = false,
  lodging,
  roomInfo,
}: IHotelSummaryPanelProps): JSX.Element => {
  const { t, formatDateTime } = useI18nContext();

  const formatDate = (date: DayJsInput | null): string => {
    return formatDateWithLocale(date, formatDateTime, dateFormat);
  };
  // const [image] = getImageUrlsArray(lodging.media);

  return (
    <Box
      className={clsx("hotel-summary-panel", { mobile: isMobile }, className)}
    >
      {/* {!hideImage && !isMobile && (
        <img
          alt="lodging-media"
          className={clsx("hotel-image", { "full-image": fullImage })}
          src={image}
        />
      )} */}
      <Box className="booking-details">
        {!hideStarRating && <HotelStarRating rating={lodging.starRating} />}
        <HotelName name={lodging.name} />
        <HotelAddress showFullAddress lodgingData={lodging} />
        {!isMobile && roomInfo && (
          <HotelRoomName
            hideImage
            roomInfo={roomInfo}
            isMobile={isMobile}
            // image={image}
          />
        )}
        <Grid
          container
          alignItems="center"
          className="booking-dates"
          direction="row"
          spacing={2}
        >
          <Grid item>
            <Icon className="calendar-icon" name={IconName.Calendar} />
          </Grid>
          <Grid item>
            <Box className="booking-dates-header">{t("checkIn")}</Box>
            <Box className="booking-dates-date">{formatDate(checkIn)}</Box>
          </Grid>
          <Grid item>
            <Icon className="calendar-icon" name={IconName.Calendar} />
          </Grid>
          <Grid item>
            <Box className="booking-dates-header">{t("checkOut")}</Box>
            <Box className="booking-dates-date">{formatDate(checkOut)}</Box>
          </Grid>
        </Grid>
        {isMobile && roomInfo && (
          <HotelRoomName
            roomInfo={roomInfo}
            isMobile={isMobile}
            // image={image}
          />
        )}
      </Box>
      {actions}
    </Box>
  );
};

export default HotelSummaryPanel;
